import { cn } from '@rocco/utils/cn';
import { useEffect } from 'react';
import { useFetcher } from 'react-router';
import type { ImageColumnProps } from '@modules/page-builder/types';
import type { PageBuilderGetModuleProductAction } from '~/routes/remix-api.page-builder.get-module-product';
import { QUERY_MODULE_PRODUCT } from '~/routes/remix-api.page-builder.get-module-product';
import { ProductTile } from '@rocco/ui/tile-product/components/ProductTile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { RevampSectionHeading } from './RevampSectionHeading';

/**
 * One product tile, in the product directory's own card.
 *
 * Rocco's `ProductTile` rather than a card of this module's own, so a product
 * in an article looks like the same product in the directory. Not the
 * directory's `DirectoryProductTile` wrapper — that one carries the browse
 * page's moodboard, bulk-enquiry, library-action and view-transition
 * contexts, none of which an article body mounts.
 *
 * The card shows the product and its supplier. It carries no price and no
 * colour swatches by design — those belong to the detail page — so the
 * module's own deferred lookup supplies everything the card needs, and the
 * fields it does not read are left empty rather than invented.
 */
const CollectionTile = ({ column }: { column: ImageColumnProps }) => {
    const fetcher = useFetcher<PageBuilderGetModuleProductAction>();

    useEffect(() => {
        if (
            column.productId &&
            fetcher.state === 'idle' &&
            fetcher.data === undefined
        ) {
            fetcher.load(
                `${QUERY_MODULE_PRODUCT}?productId=${encodeURIComponent(column.productId)}`,
            );
        }
    }, [fetcher, column.productId]);

    const product = fetcher.data;
    // The column's caption stands in until the lookup lands, as it did before.
    const title = product?.productName || column.caption || '';

    const model: ProductTileViewModel = {
        id: Number(product?.id ?? column.productId ?? 0),
        title,
        // The editor's own cut-out, which is what the module is built from.
        images: column.imageUrl ? [{ src: column.imageUrl, alt: title }] : [],
        video: [],
        link: product?.productLink ?? '',
        price: null,
        variants: null,
        hasAwards: false,
        draft: false,
        badge: null,
        pendingApproval: false,
        canPurchase: false,
        professional: {
            id: 0,
            name: product?.professionalName ?? '',
            link: product?.professionalLink ?? '',
        },
        seoDescription: '',
    };

    return (
        <ProductTile
            model={model}
            aspectRatio="product"
            section="ArticleCollection"
        />
    );
};

interface RevampCollectionModuleProps {
    columns: ImageColumnProps[];
    /** Optional ruled heading, from the module's h3 title column. */
    titleHTML?: string;
}

/**
 * "Single row collection" (Figma 2479:70454): a row of product tiles under the
 * module's optional ruled heading. Only the titled presets carry a heading
 * column, so a collection built from the plain gallery presets renders as a
 * bare grid.
 */
export const RevampCollectionModule = ({
    columns,
    titleHTML,
}: RevampCollectionModuleProps) => {
    const products = columns.filter((column) => column.imageUrl);
    if (products.length === 0) return null;

    return (
        <section className="w-full">
            <RevampSectionHeading contentHTML={titleHTML} />
            <div
                className={cn(
                    'grid grid-cols-2 gap-x-4 gap-y-10 px-4.5 md:grid-cols-4 md:gap-6 md:px-[3rem]',
                    titleHTML && 'mt-6 md:mt-[1.5rem]',
                )}
            >
                {products.map((column) => (
                    <CollectionTile key={column.id} column={column} />
                ))}
            </div>
        </section>
    );
};
